import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DashboardGoal, TodayDashboardResponse } from "@consistency-circle/shared";
import { apiClient } from "../../../lib/api";

function recomputeCounts(goals: DashboardGoal[]) {
  const allSlots = goals.flatMap((g) => g.slots);
  const completedCount = allSlots.filter((s) => s.status === "DONE").length;
  const totalCount = allSlots.filter((s) => s.status !== "SKIPPED").length;
  const completionPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  return { completedCount, totalCount, completionPercent };
}

function applySlotStatus(
  old: TodayDashboardResponse,
  goalId: string,
  slotId: string,
  newStatus: "DONE" | "DUE_LATER" | "SKIPPED",
  completionKind: "FULL" | "RECOVERY" | null = null,
): TodayDashboardResponse {
  const goals = old.goals.map((g) =>
    g.id !== goalId
      ? g
      : {
          ...g,
          slots: g.slots.map((s) =>
            s.id !== slotId
              ? s
              : {
                  ...s,
                  status: newStatus,
                  completedAt: newStatus === "DONE" ? new Date().toISOString() : null,
                  completionKind: newStatus === "DONE" ? completionKind ?? "FULL" : null,
                },
          ),
        },
  );
  return { ...old, goals, ...recomputeCounts(goals) };
}

type OptimisticContext = { prev: TodayDashboardResponse | undefined };

function makeOptimisticOptions(
  queryClient: ReturnType<typeof useQueryClient>,
  goalId: string,
  slotId: string,
  newStatus: "DONE" | "DUE_LATER" | "SKIPPED",
  completionKind: "FULL" | "RECOVERY" | null = null,
) {
  return {
    onMutate: async (): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: ["today-dashboard"] });
      const prev = queryClient.getQueryData<TodayDashboardResponse>(["today-dashboard"]);
      if (prev) {
        queryClient.setQueryData(
          ["today-dashboard"],
          applySlotStatus(prev, goalId, slotId, newStatus, completionKind),
        );
      }
      return { prev };
    },
    onError: (_: unknown, _vars: void, context: OptimisticContext | undefined) => {
      if (context?.prev) queryClient.setQueryData(["today-dashboard"], context.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["today-dashboard"] });
    },
  };
}

export function useSlotMutations(goalId: string, slotId: string) {
  const queryClient = useQueryClient();

  const complete = useMutation({
    mutationFn: () => apiClient.completeDailyGoalSlotToday(goalId, slotId),
    ...makeOptimisticOptions(queryClient, goalId, slotId, "DONE"),
  });

  const completeRecovery = useMutation({
    mutationFn: () => apiClient.completeDailyGoalSlotToday(goalId, slotId, "RECOVERY"),
    ...makeOptimisticOptions(queryClient, goalId, slotId, "DONE", "RECOVERY"),
  });

  const undo = useMutation({
    mutationFn: () => apiClient.undoDailyGoalSlotToday(goalId, slotId),
    ...makeOptimisticOptions(queryClient, goalId, slotId, "DUE_LATER"),
  });

  const skip = useMutation({
    mutationFn: () => apiClient.skipDailyGoalSlotToday(goalId, slotId),
    ...makeOptimisticOptions(queryClient, goalId, slotId, "SKIPPED"),
  });

  const unskip = useMutation({
    mutationFn: () => apiClient.unskipDailyGoalSlotToday(goalId, slotId),
    ...makeOptimisticOptions(queryClient, goalId, slotId, "DUE_LATER"),
  });

  const isLoading =
    complete.isPending ||
    completeRecovery.isPending ||
    undo.isPending ||
    skip.isPending ||
    unskip.isPending;

  return { complete, completeRecovery, undo, skip, unskip, isLoading };
}
